import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { diagnosticsManager } from 'singletons';
import { TeamStore } from 'nomst/Stores/team.store';
import { IEmployee } from 'typings/employee';
import { IEmployeeLoopEntry,
    IDiagnosticSummary,
    IMasterAccountUser,
    IDetailedEvent,
    IEmployeeComparisonResults,
    IEmailIssues,
    IError,
    IListenerErrors,
    IMissingUser, 
    IDuplicateJob } from 'typings/diagnostics';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import './diagnosticsView.scss';
import { EventTypes, EntityTypes, AmountCode, CycleStatus } from 'typings/enums';
import { eligibilityOverride, eventStore, dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { Amount } from 'nomst/Stores/Decision/amount';
import { configurationsStore, cycleRules } from 'utils/MobxDataManager/singletons';
import { Select } from '@payscale/design';

interface DiagnosticsViewProps {
    store: TeamStore;
}

interface DiagnosticsViewState {
    isLoaded: boolean;
    summary: Nullable<IDiagnosticSummary>;
    budgetFields: ICycleBudgetField[];
    budgets: CycleBudget[];
    employeeSourceIds: Map<string, string>;
    employeeEvents: IDetailedEvent[];
    selectedEventIndex: number;
    employeeId: string;
    terminatedEmployeeIds: string;
    employeeComparisonResults: Nullable<IEmployeeComparisonResults>;
    emailIssues: Nullable<IEmailIssues>;
    budgetCycleId: Nullable<number>;
    validationErrors: IError[];
    listenerErrors: IListenerErrors[];
    listenerErrorText: Nullable<string>;
    hrAdmin: Nullable<IHrReviewerUser>;
    backfillLink: Nullable<string>;
    dupeJobs: Nullable<IDuplicateJob[]>;
}

const processPath = (path: string): string => {
    return (path || '')
        .split(',')
        .filter(s => !!s)
        .join(' -> ');
};

const payHistoryFilterOptions = [
    { value: '', text: 'All pay changes' },
    { value: AmountCode.Merit, text: 'Merit' },
    { value: AmountCode.Bonus, text: 'Bonus' },
    { value: AmountCode.MarketAdjustment, text: 'Market Adjustment' },
    { value: AmountCode.LumpSum, text: 'Lump Sum' },
    { value: AmountCode.Promotion, text: 'Promotion' },
    { value: AmountCode.Stock, text: 'Stock' },
    { value: AmountCode.VariablePay, text: 'Variable Pay' }
];

export class DiagnosticsView extends React.Component<DiagnosticsViewProps, DiagnosticsViewState> {
    amountNames: Map<string, string>;

    constructor(props: DiagnosticsViewProps) {
        super(props);

        this.state = {
            isLoaded: false,
            summary: {
                employeeLoops: [],
                employeesWithoutManagers: [],
                missingUsers: [],
                employeesWithMissingData: [],
                conversions: [],
                countries: []
            },
            budgetFields: [],
            budgets: [],
            employeeSourceIds: new Map<string, string>(),
            employeeEvents: [],
            selectedEventIndex: 0,
            employeeId: '',
            terminatedEmployeeIds: '',
            employeeComparisonResults: {
                labEmployeesCount: 0,
                teamEmployeesCount: 0,
                comment: '',
                missingInLab: [],
                missingInTeam: []
            },
            emailIssues: {
                missing: [],
                dupeEmails: {},
                allEmails: {}
            },
            budgetCycleId: null,
            validationErrors: [],
            listenerErrors: [],
            listenerErrorText: null,
            hrAdmin: null,
            backfillLink: null,
            dupeJobs: null
        };

        const filterOptions = [...payHistoryFilterOptions];
        this.amountNames = filterOptions.reduce((map, obj) => {
            map.set(obj.value, obj.text);
            return map;
        }, new Map<string, string>());

        this.renderAmountRow = this.renderAmountRow.bind(this);
        this.handleTextChange = this.handleTextChange.bind(this);
        this.updateBasePayRangeStatus = this.updateBasePayRangeStatus.bind(this);
        this.regenerateWorkflow = this.regenerateWorkflow.bind(this);
        this.reprocessPayHistory = this.reprocessPayHistory.bind(this);
        this.reprocessEmployees = this.reprocessEmployees.bind(this);
        this.getEvents = this.getEvents.bind(this);
        this.getEventBody = this.getEventBody.bind(this);
        this.renderEventRow = this.renderEventRow.bind(this);
        this.setEmployeeId = this.setEmployeeId.bind(this);
        this.handleTerminate = this.handleTerminate.bind(this);
        this.onTerminateChange = this.onTerminateChange.bind(this);
        this.flushRedisCache = this.flushRedisCache.bind(this);
        this.backfill = this.backfill.bind(this);
        this.notifyManagers = this.notifyManagers.bind(this);
        this.closeCycle = this.closeCycle.bind(this);
        this.publishCycle = this.publishCycle.bind(this);
        this.reopenClosedCycle = this.reopenClosedCycle.bind(this);
        this.forceDeleteCycle = this.forceDeleteCycle.bind(this);
        this.setBudgetCycleId = this.setBudgetCycleId.bind(this);
        this.forceDeleteBudget = this.forceDeleteBudget.bind(this);
        this.setErrorText = this.setErrorText.bind(this);
        this.getExceptionText = this.getExceptionText.bind(this);
        this.renderErrorRow = this.renderErrorRow.bind(this);
        this.resetUserIds = this.resetUserIds.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const { masterAccountId } = this.props.store;
        const summaryTask = diagnosticsManager.getDiagnostics(masterAccountId);
        const configTask = dataManagerFactory.getConfigurationDataManager().getCurrentCycleConfiguration(masterAccountId);
        const backfillLinkTask = diagnosticsManager.getBackfillLink(masterAccountId);

        const employeeComparisonTask = diagnosticsManager.compareEmployees(masterAccountId);
        const emailIssuesTask = diagnosticsManager.getEmailIssues(masterAccountId);
        const validationErrorsTask = diagnosticsManager.getValidationErrors(masterAccountId);
        const listenerErrorsTask = diagnosticsManager.getListenerErrors(masterAccountId);
        const dupeJobsTask = diagnosticsManager.getDuplicateJobs(masterAccountId);

        const config = await configTask;
        const budgetFields: ICycleBudgetField[] = config?.budgetFields ? config.budgetFields : [];
        const budgets: CycleBudget[] = config && config.budgets ? config.budgets.map(budget => new CycleBudget(budget)) : [];

        const hrApproverTask = dataManagerFactory.getConfigurationDataManager().getHrApproverUser(masterAccountId, config?.cycleId || 0);

        this.setState({ budgetFields,
            budgets,
            summary: await summaryTask,
            isLoaded: true,
            employeeComparisonResults: await employeeComparisonTask,
            emailIssues: await emailIssuesTask,
            validationErrors: await validationErrorsTask,
            listenerErrors: await listenerErrorsTask,
            hrAdmin: await hrApproverTask,
            backfillLink: await backfillLinkTask,
            dupeJobs: await dupeJobsTask
        });
    }

    render(): React.ReactNode {
        const { isLoaded } = this.state;
        return isLoaded ?
            this.renderSummary() :
            <div>Loading...</div>;
    }

    renderSummary(): React.ReactNode {
        return (
            <div className="diagnostics-view">
                <>
                    {this.renderAccountName()}
                    {this.renderMaid()}
                    {this.renderHrAdmin()}
                    {this.renderCycleStatus()}
                    {this.renderEmployeeComparison()}
                    {this.renderValidationErrors()}
                    {this.renderDuplicateJobs()}
                    {this.renderLoops()}
                    {this.renderManagers()}
                    {this.renderMissingEmployees()}
                    {this.renderMissingData()}
                    {this.renderDuplicateEmails()}
                    {this.renderMissingUsers()}
                    {this.renderCountriesAndCurrencies()}
                    {this.renderBudgetAmounts()}
                </>
                <div>
                    {this.renderEventSection()}
                </div>
                <div>
                    <button onClick={this.updateBasePayRangeStatus}>Resync Base Pay Range</button>
                    <button onClick={this.regenerateWorkflow}>Regenerate Workflow</button>
                    <button onClick={this.resetUserIds}>Reset User Ids</button>
                </div>
                {this.renderTerminateField()}
                {this.renderListenerExceptions()}
                <div>
                    <p>
                        The following actions will affect the customer’s current cycle.
                    </p>
                    {this.renderRedisClearCacheButton()}
                    {this.renderNotifyManagersButton()}
                    {this.renderCloseCycleButton()}
                    {this.renderPublishCycleButton()}
                    {this.renderRevertCloseCycleButton()}
                    {this.renderForceDeleteCycleButton()}
                    {this.renderReprocessPayHistoryButton()}
                    {this.renderReprocessEmployeesButton()}
                    {this.renderBackfillButton()}
                    {this.renderBackfillLink()}
                    {this.renderDeleteBudget()}
                </div>
            </div>
        );
    }

    renderAccountName(): React.ReactNode {
        return (
            <div className="account-name">
                <h3>Account Name</h3>
                <div>{window.mx.jsContext.accountName}</div>
            </div>
        );
    }

    renderMaid(): React.ReactNode {
        return (
            <div className="maid">
                <h3>MAID</h3>
                <div>{configurationsStore.masterAccountId}</div>
            </div>
        );
    }

    renderHrAdmin(): React.ReactNode {
        const { hrAdmin } = this.state;
        const hrAdminText = hrAdmin ? hrAdmin.workEmail : 'Not set';

        return (
            <div className="hr-admin">
                <h3>HR Admin</h3>
                <p>{hrAdminText}</p>
            </div>
        );
    }

    renderListenerExceptions(): React.ReactNode {
        const { listenerErrors } = this.state;

        if (!listenerErrors.length) {
            return null;
        }

        return (
            <div>
                <h3>Listener Exceptions</h3>
                <p>
                    Helps development team to easily diagnose problems by showing where the errors lie.
                </p>
                <div className="listener-errors">
                    <table>
                        <thead>
                            <tr>
                                <th>EventId</th>
                                <th>EntityId</th>
                                <th>ListenerName</th>
                                <th>Processed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listenerErrors.map(this.renderErrorRow)}
                        </tbody>
                    </table>
                    {this.renderTextArea()}
                </div>
            </div>
        );
    }

    renderLoops(): React.ReactNode {
        const { summary } = this.state;
        if (!summary || !summary.employeeLoops.length) {
            return null;
        }
        return (
            <div className="loops">
                <h3>Loops in Employee Reporting Structure</h3>
                <p>
                    The reporting hierarchy will not function correctly if there is a loop detected. To fix any issues
                    below, ensure manager IDs are correct for all employees in Lab. The table below will highlight the
                    affected employees and their manager to help you trace the loop. To resolve:
                </p>
                <ul>
                    <li>
                        Manager IDs can be updated in Lab
                    </li>
                    <li>
                        If the issue is related to the CEO, a refresh will need to take place to wipe out the manager
                        ID for the CEO
                    </li>
                </ul>
                <table>
                    <thead>
                        <tr>
                            <th>Employee ID</th>
                            <th>Manager ID</th>
                            <th>Loop Path</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.employeeLoops.map(this.renderLoopEntry)}
                    </tbody>
                </table>
            </div>
        );
    }

    renderLoopEntry(entry: IEmployeeLoopEntry): React.ReactNode {
        return (
            <tr key={entry.employeeSourceId}>
                <td>{entry.employeeSourceId}</td>
                <td>{entry.managerSourceId}</td>
                <td>{processPath(entry.path)}</td>
            </tr>
        );
    }

    renderCycleStatus(): React.ReactNode {
        const notifiedManagers = cycleRules.isCurrentCyclePendingOrPreview ? 'Not notified' : 'Notified';

        return (
            <div className="cycle-status">
                <h3>Current Cycle Status</h3>
                <h4>Cycle Status</h4>
                {configurationsStore?.currentCycleConfiguration?.status}
                <h4>Notified Managers</h4>
                {notifiedManagers}
            </div>
        );
    }

    renderManagers(): React.ReactNode {
        if (!this.state.summary) {
            return null;
        }

        const { employeesWithoutManagers } = this.state.summary;
        return (
            <div className="missing-managers">
                <h3>Missing Manager IDs</h3>
                <p>
                    The following employees have either no manager assigned to them or an invalid manager. This list
                    should contain exactly one employee with a NULL manager ID, this person should be the CEO
                    (or whomever is at the top of the hierarchy). Common errors:
                </p>
                <ul>
                    <li>
                        More than one employee with NULL manager ID indicates a parallel reporting structure.
                    </li>
                    <li>
                        No employees with NULL manager ID indicate there is no CEO (or anyone at the top of the
                        hierarchy).
                    </li>
                    <li>
                        Employees listed with a manager ID (not NULL) indicate these employees reference a manager that
                        does not exist in Lab. Check if the manager ID listed exists in Lab (casing matters), if not
                        the employee will need to be moved under a new manager ID.
                    </li>
                    <li>
                        Other reasons NULL is displayed: employee is terminated, employee is caught in validation, or
                        there are errors in the HRIS data.
                    </li>
                </ul>
                <table>
                    <thead>
                        <tr>
                            <th>Employee ID</th>
                            <th>Employee Name</th>
                            <th>Manager ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {employeesWithoutManagers.map(this.renderManagerRow)}
                    </tbody>
                </table>
            </div>
        );
    }

    renderManagerRow(employee: IEmployee): React.ReactNode {
        return (
            <tr key={employee.employeeSourceId}>
                <td>{employee.employeeSourceId}</td>
                <td>{`${employee.firstName} ${employee.lastName}`}</td>
                <td>{employee.managerSourceId || 'NULL'}</td>
            </tr>
        );
    }

    renderMissingEmployees(): React.ReactNode {
        if (!this.state.summary) {
            return null;
        }

        const { missingUsers } = this.state.summary;

        if (!missingUsers || !missingUsers.length) {
            return null;
        }

        return (
            <div className="missing-employees">
                <h3>Missing Employees</h3>
                <p>
                    These users will be able to login to Team but will not be able to see their team (likely will see
                    their teams as 0). These users are not attached to an employee in Lab. Ensure the user’s email
                    matches across user management, Lab’s manage users section and Lab employee details. If the emails
                    are the same, check for leading or trailing whitespace (“ you@payscale.com” or “you@payscale.com “)
                </p>
                <table>
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Display Name</th>
                            <th>Email</th>
                            <th>User Id</th>
                        </tr>
                    </thead>
                    <tbody>
                        {missingUsers.map(this.renderMissingUserRow)}
                    </tbody>
                </table>
            </div>
        );
    }

    renderErrorRow(error: IListenerErrors, index: number): React.ReactNode {
        const onClick = () => this.setErrorText(error.errorText);
        return (
            <tr key={index}>
                <td><button className='pxl-link' onClick={onClick}>{error.eventId}</button></td>
                <td>{error.id}</td>
                <td>{error.listenerName}</td>
                <td>{error.processedDate}</td>
            </tr>
        );
    }

    renderTextArea(): React.ReactNode {
        const { listenerErrors } = this.state;

        if (!listenerErrors.length) {
            return null;
        }

        return (
            <div>
                <textarea className="exception-details" rows={40} cols={80} defaultValue={this.getExceptionText()} />
            </div>
        );
    }

    getExceptionText(): string {
        const { listenerErrorText, listenerErrors } = this.state;
        if (!listenerErrors || !listenerErrorText ) {
            return '';
        }
        return listenerErrorText;
    }

    renderValidationErrors(): React.ReactNode {
        const { validationErrors } = this.state;

        if (!validationErrors.length) {
            return null;
        }

        return (
            <div className="validation-errors">
                <h3>Invalid Employee Data</h3>
                <p>
                    The following employees have been caught in validation. The employee IDs listed have one or more
                    errors and must be fixed in Lab before they will be usable in Team. If an error has a length of 0
                    it means the field is empty for this employee ID within Lab.
                </p>
                <table>
                    <thead>
                        <tr>
                            <th>Employee ID</th>
                            <th>Errors</th>
                            <th>Processed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {validationErrors.map(e => (
                            <tr key={e.id}>
                                <td>{e.id}</td>
                                <td>{this.processErrorText(e.errorText)}</td>
                                <td>{e.processedDate}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    processErrorText(errorText: string): React.ReactNode {
        const errors = errorText.split(/[\r\n]+/);

        return (
            <ul>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
        );
    }

    renderMissingUserRow(user: IMasterAccountUser): React.ReactNode {
        return (
            <tr key={user.userId}>
                <td>{user.firstName}</td>
                <td>{user.lastName}</td>
                <td>{user.displayName}</td>
                <td>{user.emailAddress}</td>
                <td>{user.userId}</td>
            </tr>
        );
    }

    renderDuplicateJobs(): React.ReactNode {
        const { dupeJobs } = this.state;
        if (!dupeJobs?.length) {
            return null;
        }

        return (
            <div className="duplicate-jobs">
                <h3>Duplicate Jobs</h3>
                <p>
                    Duplicate jobs found in Lab, submit a ticket with the identified duplicate jobs with a request to reindex the site.
                </p>
                <table>
                    <thead>
                        <tr>
                            <th>Job Code</th>
                            <th>Job Code Key</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dupeJobs.map(job => (
                            <tr key={`${job.code}-${job.jobCodeKey}`}>
                                <td>{job.code}</td>
                                <td>{job.jobCodeKey}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderBudgetAmounts(): React.ReactNode {
        const { budgetFields } = this.state;
        if (!budgetFields || !budgetFields.length) {
            return null;
        }

        return (
            <div className="amounts">
                <h3>Employee Eligibility</h3>
                <p>
                    To mark employee ineligible, enter employee IDs and separate each ID by a new line.
                    There is no max number that can be added.
                </p>
                <table cellPadding="5px">
                    <thead>
                        <tr>
                            <th>Budget Name</th>
                            <th>Amount Code</th>
                            <th>Employee List</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {budgetFields.map(this.renderAmountRow)}
                    </tbody>
                </table>
            </div>
        );
    }

    renderAmountRow(budgetField: ICycleBudgetField): React.ReactNode {
        const { budgets, employeeSourceIds } = this.state;
        const textChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
            this.handleTextChange(budgetField.amountCode, event);
        const id = employeeSourceIds.get(budgetField.amountCode);
        const budget = budgets.find(b => b.budgetCycleId === budgetField.budgetCycleId);

        if (!budget) {
            return null;
        }
        const saveClick = () => this.updateAmount(id, budgetField);

        return (
            <tr key={budgetField.amountCycleId}>
                <td>{budget.name}</td>
                <td>{this.amountNames.get(budgetField.amountCode)}</td>
                <td>
                    <textarea
                        rows={6}
                        onChange={textChange}
                    />
                </td>
                <td>
                    <button
                        onClick={saveClick}
                    >
                        Mark Ineligible
                    </button>
                </td>
            </tr>
        );
    }

    updateAmount(employeeSourceIds: NullOrUndefined<string>, budgetField: ICycleBudgetField): Promise<void> {
        if (!employeeSourceIds) {
            return Promise.resolve();
        }
        const ids = employeeSourceIds.split(/[\r\n]+/).map(s => s.trim());
        const events = [];

        for (const id of ids) {
            if (!id) {
                continue;
            }
            const employee = {
                employeeSourceId: id,
                // This is the min possible to date to force the event to get sent.
                payEffectiveDateType: () => new Date(-8640000000000000)
            } as unknown as IEmployee;

            const updatedEmployeeAmount = {
                amountCycleId: budgetField.amountCycleId,
                amountCode: budgetField.amountCode,
                isEligible: false
            } as Amount;

            events.push(eligibilityOverride
                .getOverrideEligibilityEvent(employee, updatedEmployeeAmount, true));
            events.push(eligibilityOverride
                .getResetAmountEvent(employee, budgetField.amountCycleId, budgetField.amountCode, 0));
            if (budgetField.budgetCycleId) {
                events.push(eligibilityOverride
                    .getUpdateBudgetEvent(employee, budgetField.budgetCycleId, 0, 0));
            }
        }
        return eventStore.sendBatchEvents(
            configurationsStore.currentConvertedConfiguration.cycleId, events as IEmployeeEvent[]);
    }

    renderTerminateField(): React.ReactNode {
        return (
            <div className="terminate">
                <p>
                    To terminate employees, enter employee ids separated by new lines (not commas!) in the form below
                    and click the terminate button. Note that sending a long list of ids might cause a timeout - if so,
                    you can try breaking up the list and sending a smaller number of employee ids at a time.
                </p>
                <textarea rows={6} onChange={this.onTerminateChange} />
                <button onClick={this.handleTerminate}>TERMINATE THESE EMPLOYEES</button>
            </div>
        );
    }

    renderReprocessPayHistoryButton(): React.ReactNode {
        const { user } = this.props.store;

        if (!user.isTeamDev) {
            return null;
        }

        return (
            <button
                className="reprocess-payhistory-btn"
                onClick={this.reprocessPayHistory}
            >Wipe and Reprocess PayHistory
            </button>
        );

    }

    renderReprocessEmployeesButton(): React.ReactNode {
        return (
            <button
                className="reprocess-employees-btn"
                onClick={this.reprocessEmployees}
            >Reprocess Employees
            </button>
        );

    }

    renderRedisClearCacheButton(): React.ReactNode {
        const { user } = this.props.store;

        if (!user.isTeamDev) {
            return null;
        }

        return (
            <button
                className="clear-cache-btn"
                onClick={this.flushRedisCache}
            >Flush Redis Cache
            </button>
        );

    }

    renderBackfillButton(): React.ReactNode {
        return (
            <>
                <p>
                    DOING THIS WILL DELETE ALL EMPLOYEES OUT OF TEAM. YOU MUST BACKFILL.
                </p>
                <button
                    className="backfill-btn"
                    onClick={this.backfill}
                >
                    Delete ALL Employees
                </button>
            </>
        );
    }

    renderBackfillLink(): React.ReactNode {
        const { backfillLink } = this.state;
        if (!backfillLink) {
            return null;
        }

        return (
            <>
                <p>
                    You must login to lab before clicking the link below to run a backfill for this account.
                </p>
                <a
                    className="backfill-link"
                    href={backfillLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Backfill
                </a>
            </>
        );
    }

    renderNotifyManagersButton(): React.ReactNode {
        return (
            <button
                className="notify-manager-btn"
                title="Re-emails every manager in the current cycle"
                onClick={this.notifyManagers}
            >
                Notify Managers
            </button>
        );
    }

    renderCloseCycleButton(): React.ReactNode {
        if (cycleRules.isCurrentCycleClosedOrPublished) {
            return null;
        }

        return (
            <button
                className="close-cycle-btn"
                title="If an account is stuck in the closing state, will send the close cycle event"
                onClick={this.closeCycle}
            >
                Close Cycle
            </button>
        );
    }

    renderPublishCycleButton(): React.ReactNode {
        if (configurationsStore.currentCycleConfiguration?.status !== CycleStatus.Closed) {
            return null;
        }

        return (
            <button
                className="publish-cycle-btn"
                onClick={this.publishCycle}
            >
                Sync to Insight Lab
            </button>
        );
    }

    renderRevertCloseCycleButton(): React.ReactNode {
        const { user } = this.props.store;
        if (!user.isTeamDev || !cycleRules.isCurrentCycleClosedOrPublished) {
            return null;
        }

        return (
            <button
                className="reopen-closed-cycle-btn"
                title="Cycle has been closed and will only reopen if no pending cycles are available"
                onClick={this.reopenClosedCycle}
            >
                Reopen Closed Cycle
            </button>
        );
    }

    renderDeleteBudget(): React.ReactNode {
        const { budgets } = this.state;

        if (!budgets) {
            return null;
        }

        return (
            <div className="delete-budget">
                {this.renderBudgetDropdown()}
                {this.renderForceDeleteBudgetButton()}
            </div>
        );
    }

    renderForceDeleteCycleButton(): React.ReactNode {
        const { user } = this.props.store;

        if (!user.isTeamDev) {
            return null;
        }

        return (
            <button
                className="force-delete-cycle-btn"
                title="Will delete cycle and all decisions made"
                onClick={this.forceDeleteCycle}
            >
                Force Delete Cycle With Manager Decisions!
            </button>
        );
    }

    renderBudgetDropdown(): React.ReactNode {
        const { budgets } = this.state;

        if (!budgets) {
            return null;
        }

        const budgetOptions = [{
            text: 'Select a budget to delete',
            value: -1
        }];

        budgetOptions.push(...budgets.map((budget) => {
            return {
                text: budget.name,
                value: budget.budgetCycleId
            };
        }));

        return (
            <Select
                id="budgetNames"
                className="budget-dropdown col-xs-3"
                onChange={this.setBudgetCycleId}
                options={budgetOptions}
            />
        );
    }

    renderForceDeleteBudgetButton(): React.ReactNode {
        const { budgets } = this.state;

        if (!budgets) {
            return null;
        }

        return (
            <button
                className="force-delete-budget-btn"
                title="Will delete the budget and all decisions made for the current cycle"
                onClick={this.forceDeleteBudget}
            >
                Force Delete Budget with Decisions
            </button>
        );
    }

    onTerminateChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({ terminatedEmployeeIds:  event.target.value });
    }

    async handleTerminate(): Promise<void> {
        const { terminatedEmployeeIds } = this.state;

        const ids = terminatedEmployeeIds.split(/[\r\n]+/).map(s => s.trim());

        if (!confirm('Are you sure!?')) {
            return;
        }

        const events = ids
            .filter(id => !!id)
            .map(id => ({
                employeeSourceId: id,
                useEmployeeTopic: true,
                eventType: EventTypes.Terminate,
                entityType: EntityTypes.Employee,
                userId: this.props.store.user.userId,
                data: {
                    terminationDate: new Date(Date.now()).toUTCString(),
                    reason: 'Marked as terminated by PS Support'
                },
                metadata: {
                    affectWorkflow: true
                }
            } as IEmployeeEvent));

        await eventStore.sendBatchEvents(null, events);
    }

    handleTextChange(id: string, event: React.ChangeEvent<HTMLTextAreaElement>): void {
        const { employeeSourceIds } = this.state;
        employeeSourceIds.set(id, event.target.value);
        this.setState({ employeeSourceIds });
    }

    async updateBasePayRangeStatus(): Promise<void> {
        await diagnosticsManager.updateBasePayRangeStatus(
            configurationsStore.masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async regenerateWorkflow(): Promise<void> {
        await diagnosticsManager.regenerateWorkflow(
            configurationsStore.masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async resetUserIds(): Promise<void> {
        await diagnosticsManager.resetUserIds(configurationsStore.masterAccountId);
    }

    async reprocessPayHistory(): Promise<void> {
        await diagnosticsManager.reprocesspayHistory(
            configurationsStore.masterAccountId);
    }

    async reprocessEmployees(): Promise<void> {
        await diagnosticsManager.reprocessEmployees(
            configurationsStore.masterAccountId);
    }

    async flushRedisCache(): Promise<void> {
        await diagnosticsManager.flushRedisCache(configurationsStore.masterAccountId);
    }

    async notifyManagers(): Promise<void> {
        await diagnosticsManager.notifyManagers(
            configurationsStore.masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async closeCycle(): Promise<void> {
        await diagnosticsManager.closeCycle(
            configurationsStore.masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async publishCycle(): Promise<void> {
        await diagnosticsManager.publishCycle(
            configurationsStore.masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async backfill(): Promise<void> {
        await diagnosticsManager.backfill(configurationsStore.masterAccountId);
    }

    async reopenClosedCycle(): Promise<void> {
        await diagnosticsManager.reopenClosedCycle(
            configurationsStore._masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async forceDeleteCycle(): Promise<void> {
        await diagnosticsManager.forceDeleteCycle(
            configurationsStore._masterAccountId,
            configurationsStore.currentCycleConfiguration?.cycleId);
    }

    async forceDeleteBudget(): Promise<void> {
        const { budgetCycleId } = this.state;

        if (budgetCycleId !== null) {
            await diagnosticsManager.forceDeleteBudget(
                configurationsStore._masterAccountId,
                configurationsStore.currentCycleConfiguration?.cycleId,
                budgetCycleId);
        }

        this.setState({ budgetCycleId: null });
    }

    renderEventSection(): React.ReactNode {
        const { employeeEvents, employeeId } = this.state;
        return (
            <>
                <h3>Employee Events</h3>
                <p>
                    See all events for a specific employee
                </p>
                <label>Employee Source Id:
                    <input type="text" value={employeeId} onChange={this.setEmployeeId} />
                </label>
                <button onClick={this.getEvents}>Get Events</button>
                <div className="employee-events">
                    <table>
                        <thead>
                            <tr>
                                <th>Event Id</th>
                                <th>Event Type</th>
                                <th>Global Sequence</th>
                                <th>Entity Id</th>
                                <th>Event Created Utc</th>
                                <th>Namespace</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeEvents.map(this.renderEventRow)}
                        </tbody>
                    </table>
                    <div>
                        <textarea rows={40} cols={80} defaultValue={this.getEventBody()} />
                    </div>
                </div>
            </>
        );
    }

    setEmployeeId(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({ employeeId: e.target.value});
    }

    setBudgetCycleId(_: undefined, value: string): void {
        this.setState({ budgetCycleId: parseInt(value, 10) });
    }

    setErrorText(errorText: string): void {
        this.setState({ listenerErrorText: errorText });
    }

    getEventBody(): string {
        const { employeeEvents, selectedEventIndex } = this.state;
        if (!employeeEvents || !employeeEvents[selectedEventIndex]) {
            return '';
        }

        return JSON.stringify(employeeEvents[selectedEventIndex], null, 2);
    }

    renderEventRow(e: IDetailedEvent, index: number): React.ReactNode {
        const date = new Date(e.eventCreatedUtc).toLocaleString();
        const onClick = () => this.setState({selectedEventIndex: index});
        return (
            <tr key={e.eventId}>
                <td ><button className='pxl-link' onClick={onClick}>{e.eventId}</button></td>
                <td>{e.eventType}</td>
                <td>{e.globalSequence}</td>
                <td>{e.entityId}</td>
                <td>{date}</td>
                <td>{e.namespace}</td>
            </tr>
        );
    }

    async getEvents(): Promise<void> {
        const { store } = this.props;
        const { employeeId } = this.state;
        if (!employeeId) {
            return;
        }
        const employeeEvents = await diagnosticsManager.getEmployeeEvents(store.masterAccountId, employeeId) || [];
        this.setState({ employeeEvents });
    }

    renderEmployeeComparison(): React.ReactNode {
        const  { employeeComparisonResults } = this.state;
        if (!employeeComparisonResults) {
            return null;
        }

        return (
            <div className="employee-comparison">
                <h3>Employee Count Comparison</h3>
                <p>
                    Ideally, the count of employees between Insight Lab and Team should match.
                </p>
                <ul>
                    <li>
                        If the employee count is higher in the Team column, there are terminated employees
                        hanging out in the database.
                    </li>
                    <li>
                        If the employee count is higher in the Lab column, employees are stuck in validation and
                        need to be checked for errors. See Invalid Employee Data table below.
                    </li>
                    <li>
                        If there is data in both EmployeeSourceIDs missing columns, the data is incorrect and will
                        need a backfill to fix.
                    </li>
                </ul>
                <table cellPadding="5px">
                    <thead>
                        <tr>
                            <th>Count of Employees In Lab</th>
                            <th>Count of Employees in Team</th>
                            <th>Comment</th>
                            <th>EmployeeSourceIds missing in Lab</th>
                            <th>EmployeeSourceIds missing in Team</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{employeeComparisonResults.labEmployeesCount}</td>
                            <td>{employeeComparisonResults.teamEmployeesCount}</td>
                            <td>{employeeComparisonResults.comment}</td>
                            <td>{employeeComparisonResults.missingInLab
                                && employeeComparisonResults.missingInLab.map(empId =>
                                    <p key={empId}>{empId}</p>)}
                            </td>
                            <td>{employeeComparisonResults.missingInTeam
                                && employeeComparisonResults.missingInTeam.map(empId =>
                                    <p key={empId}>{empId}</p>)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    renderMissingUsers(): React.ReactNode {
        const { emailIssues } = this.state;

        if (!emailIssues || !emailIssues?.missing?.length) {
            return null;
        }

        const { missing } = emailIssues;

        return (
            <div className="missing-users">
                <h3>Missing Users</h3>
                <p>
                    Users listed below cannot log in to Team because they have either:
                </p>
                <ul>
                    <li>
                        No email
                    </li>
                    <li>
                        An email that has not been added to user management
                    </li>
                    <li>
                        An email that does not appear in pending invites
                    </li>
                </ul>
                <table cellPadding="5px">
                    <thead>
                        <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                            <th>Employee ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            missing.map(m => (
                                <tr key={m.employeeSourceId}>
                                    <td>{m.firstName}</td>
                                    <td>{m.lastName}</td>
                                    <td>{m.workEmail}</td>
                                    <td>{m.employeeSourceId}</td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    renderCountriesAndCurrencies(): React.ReactNode {
        return (
            <div>
                <h3>Country and Currency</h3>
                <p>
                    The table below is useful to show any duplicated codes.
                </p>
                <div className="lab-data">
                    {this.renderCountries()}
                    {this.renderCurrencies()}
                </div>
            </div>
        );
    }

    renderDuplicateEmails(): React.ReactNode {
        const { emailIssues } = this.state;

        if (!emailIssues || !emailIssues?.dupeEmails?.length) {
            return null;
        }

        const { dupeEmails } = emailIssues;

        return (
            <div className="dupe-emails">
                <h3>Duplicate Emails</h3>
                <p>
                    This is a list of all emails in the database that are
                    assigned to multiple managers
                </p>
                <table cellPadding="5px">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Employee Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            Object.keys(dupeEmails).map(d => (
                                <tr key={d}>
                                    <td>{d}</td>
                                    <td>{this.renderMissingEmailRow(dupeEmails[d])}</td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        );
    }

    renderMissingEmailRow(dupes: IMissingUser[]): React.ReactNode {
        return dupes.map(d => (
            <p key={d.employeeSourceId}>
                {`${d.firstName} ${d.lastName} (${d.employeeSourceId})`}
            </p>
        ));
    }

    renderMissingData(): React.ReactNode {
        const { summary } = this.state;
        if (!summary || !summary.employeesWithMissingData || !summary.employeesWithMissingData.length) {
            return null;
        }

        return (
            <>
                <h3>Employees With Missing Data</h3>
                <p>
                    The following employees are missing data in Employee DB. Please fix anything with a sad face
                    in lab or customers will see weird things!
                </p>
                <div className="missing-data">
                    <table cellPadding="5px">
                        <thead>
                            <tr>
                                <th>Id</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Employee Class</th>
                                <th>Country Code</th>
                                <th>Currency Code</th>
                                <th>Job Code</th>
                                <th>Job Code Key</th>
                                <th>Hire Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.employeesWithMissingData.map(e => (
                                <tr key={e.employeeSourceId}>
                                    <td>{e.employeeSourceId}</td>
                                    <td>{this.formatCell(e.firstName)}</td>
                                    <td>{this.formatCell(e.lastName)}</td>
                                    <td>{e.workEmail}</td>
                                    <td>{this.formatCell(e.employeeClass)}</td>
                                    <td>{this.formatCell(e.countryCode)}</td>
                                    <td>{this.formatCell(e.isoCurrencyCode)}</td>
                                    <td>{this.formatCell(e.jobCode)}</td>
                                    <td>{this.formatCell(e.jobCodeKey)}</td>
                                    <td>{this.formatCell(e.hireDate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    }

    renderCountries(): React.ReactNode {
        const { summary } = this.state;
        if (!summary || !summary.countries || !summary.countries.length) {
            return null;
        }

        return (
            <div className="countries">
                <table>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Name</th>
                            <th>ISO Currency Code</th>
                            <th>Weekly Standard Hours</th>
                            <th>Annual Standard Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.countries.map(c => (
                            <tr key={c.code}>
                                <td>{c.code}</td>
                                <td>{c.name}</td>
                                <td>{c.isoCurrencyCode}</td>
                                <td>{c.weeklyStandardWorkHours}</td>
                                <td>{c.annualStandardWorkHours}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderCurrencies(): React.ReactNode {
        const { summary } = this.state;
        if (!summary || !summary.conversions || !summary.conversions.length) {
            return null;
        }

        return (
            <div className="conversions">
                <table>
                    <thead>
                        <tr>
                            <th>Source</th>
                            <th>Target</th>
                            <th>Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.conversions.map(c => (
                            <tr key={c.source + c.target}>
                                <td>{c.source}</td>
                                <td>{c.target}</td>
                                <td>{c.rate}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    private formatCell(data: string): React.ReactNode {
        return data || <img alt="This data is missing" src={require('contents/icon-sadface.svg')} className="sad-face-icon"/>;
    }
}

export default inject('store')(observer(DiagnosticsView));
